import type {
  AdditionalCategoriesConfiguration,
  BoltConfiguration,
  ConnectorBindingConfiguration,
  MenuConfiguration,
  ProcessPropertiesConfiguration,
  Screen,
} from '../../../shared/types';
import type { Attribute, ObjectType } from '../../../shared/types';
import {
  getBoltProfileMappingDefinitions,
  getBoltProfileObjectTypeName,
} from '../../../shared/lib/boltProfileMapping';

export const REQUIRED_ATTRIBUTE_NAMES = ['OBJECT-TYPE', 'SKEY', 'PIPING-SPEC'] as const;

export type MappingInsightTone = 'success' | 'warning' | 'info' | 'default';
export type MappingSourceKey =
  | 'workflow'
  | 'connectors'
  | 'bolts'
  | 'categories'
  | 'properties'
  | 'local';

export interface MappingRowInsight {
  sourceKey: MappingSourceKey;
  sourceLabel: string;
  sourceDetail: string;
  stateLabel: string;
  stateTone: MappingInsightTone;
  stateDetail: string;
  upstreamValue: string;
  isOverridden: boolean;
  jumpTarget?: Screen;
  jumpLabel?: string;
}

export interface MappingScopeSummary {
  total: number;
  mapped: number;
  requiredOpen: number;
  overridden: number;
  dominantSourceKey: MappingSourceKey;
  dominantSourceLabel: string;
}

interface MappingBinding {
  sourceKey: Exclude<MappingSourceKey, 'local'>;
  sourceLabel: string;
  sourceDetail: string;
  jumpTarget: Screen;
  jumpLabel: string;
  value: string;
}

interface MappingWorkspaceModelInput {
  objectTypes: ObjectType[];
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
  boltConfig: BoltConfiguration;
  categoryConfig: AdditionalCategoriesConfiguration;
  processConfig: ProcessPropertiesConfiguration;
}

interface MappingWorkspaceSectionSummary {
  label: string;
  tone: MappingInsightTone;
}

interface MappingWorkspaceSections {
  processConditions: MappingWorkspaceSectionSummary;
  projectProperties: MappingWorkspaceSectionSummary;
}

const normalizeKey = (value: string): string =>
  value.trim().toUpperCase().replace(/[\s_-]/g, '');

const NORMALIZED_REQUIRED_ATTRIBUTE_NAMES = REQUIRED_ATTRIBUTE_NAMES.map(normalizeKey);

const buildBindingKey = (objectTypeName: string, pcfAttributeName: string): string =>
  `${normalizeKey(objectTypeName)}::${normalizeKey(pcfAttributeName)}`;

const getFieldValue = (field: { value?: string } | undefined): string =>
  typeof field?.value === 'string' ? field.value.trim() : '';

const isRequiredAttribute = (attribute: Attribute): boolean =>
  NORMALIZED_REQUIRED_ATTRIBUTE_NAMES.includes(normalizeKey(attribute.pcfAttributeName));

export const hasResolvedMapping = (attribute: Attribute): boolean => {
  const parameterValue = attribute.revitParam.trim();
  if (parameterValue) {
    return true;
  }

  return attribute.attributeType.trim().toLowerCase() === 'formula' && attribute.fx.trim().length > 0;
};

const buildBindings = ({
  menuConfig,
  connectorConfig,
  boltConfig,
  categoryConfig,
}: Omit<MappingWorkspaceModelInput, 'objectTypes' | 'processConfig'>): Map<string, MappingBinding> => {
  const bindings = new Map<string, MappingBinding>();

  const setBinding = (
    objectTypeName: string,
    pcfAttributeName: string,
    sourceKey: MappingBinding['sourceKey'],
    sourceLabel: string,
    sourceDetail: string,
    jumpTarget: Screen,
    jumpLabel: string,
    value: string
  ) => {
    bindings.set(buildBindingKey(objectTypeName, pcfAttributeName), {
      sourceKey,
      sourceLabel,
      sourceDetail,
      jumpTarget,
      jumpLabel,
      value: value.trim(),
    });
  };

  setBinding(
    'ALL',
    'OBJECT-TYPE',
    'workflow',
    'Workflow',
    'Seeded from workflow defaults.',
    'menu',
    'Review in Workflow',
    menuConfig.objectTypeValue
  );
  setBinding(
    'ALL',
    'SKEY',
    'workflow',
    'Workflow',
    'Seeded from workflow defaults.',
    'menu',
    'Review in Workflow',
    menuConfig.sKeyValue
  );
  setBinding(
    'ALL',
    'PIPING-SPEC',
    'workflow',
    'Workflow',
    'Seeded from workflow defaults.',
    'menu',
    'Review in Workflow',
    menuConfig.pipingSpecValue
  );
  setBinding(
    'ALL',
    'SYSTEM-GROUPING',
    'workflow',
    'Workflow',
    'Derived from workflow grouping defaults.',
    'menu',
    'Review in Workflow',
    menuConfig.systemGroupingValue
  );

  [
    ['END-POINT_1', getFieldValue(connectorConfig.endpoint1Value)],
    ['END-POINT_2', getFieldValue(connectorConfig.endpoint2Value)],
    ['END-POINT_3', getFieldValue(connectorConfig.endpoint3Value)],
    ['END-POINT_4', getFieldValue(connectorConfig.endpoint4Value)],
    ['PRESSURECLASS', getFieldValue(connectorConfig.pressureClassValue)],
    ['MATERIAL', getFieldValue(connectorConfig.materialValue)],
    ['DESIGNSTD', getFieldValue(connectorConfig.designStdValue)],
    ['ITEM-DESCRIPTION', getFieldValue(connectorConfig.itemDescriptionValue)],
    ['INSULATION-SPEC', getFieldValue(connectorConfig.insulationSpecValue)],
    ['INSULATION-THICKNESS', getFieldValue(connectorConfig.insulationThicknessValue)],
    ['TRACING-SPEC', getFieldValue(connectorConfig.tracingSpecValue)],
  ].forEach(([pcfAttributeName, value]) => {
    setBinding(
      'ALL',
      pcfAttributeName,
      'connectors',
      'Connectors',
      'Seeded from connector bindings and BOM defaults.',
      'connectorBinding',
      'Review in Connectors',
      value
    );
  });

  const isBoltBased = boltConfig.boltModelingChoice.value === 'yes';
  const boltObjectTypeName = getBoltProfileObjectTypeName(isBoltBased);
  const gasketAliasBindings = isBoltBased
    ? []
    : [
        ['ITEM-DESCRIPTION', getFieldValue(boltConfig.boltItemDescriptionValue)],
        ['MATERIAL', getFieldValue(boltConfig.boltMaterialValue)],
        ['STANDARD', getFieldValue(boltConfig.boltStandardValue)],
      ];
  const boltBindings = [
    ...getBoltProfileMappingDefinitions(isBoltBased).map(
      (definition) =>
        [definition.pcfAttributeName, getFieldValue(boltConfig[definition.field])] as const
    ),
    ...gasketAliasBindings,
  ];
  boltBindings.forEach(([pcfAttributeName, value]) => {
    setBinding(
      boltObjectTypeName,
      pcfAttributeName,
      'bolts',
      'Bolts',
      'Seeded from bolt modeling decisions.',
      'boltConnection',
      'Review in bolts',
      value
    );
  });

  [
    ['VALVE', 'TAG', getFieldValue(categoryConfig.valveTagValue)],
    ['SUPPORT', 'TAG', getFieldValue(categoryConfig.supportsTagValue)],
    ['SUPPORT', 'SSREF', getFieldValue(categoryConfig.supportsSsrefValue)],
    ['INSTRUMENT', 'TAG', getFieldValue(categoryConfig.instrumentTagValue)],
  ].forEach(([objectTypeName, pcfAttributeName, value]) => {
    setBinding(
      objectTypeName,
      pcfAttributeName,
      'categories',
      'Categories',
      'Seeded from additional category tagging.',
      'additionalCategories',
      'Review in categories',
      value
    );
  });

  return bindings;
};

const DEFAULT_SCOPE_SUMMARY: MappingScopeSummary = {
  total: 0,
  mapped: 0,
  requiredOpen: 0,
  overridden: 0,
  dominantSourceKey: 'local',
  dominantSourceLabel: 'Local',
};

const SOURCE_LABELS: Record<MappingSourceKey, string> = {
  workflow: 'Workflow',
  connectors: 'Connectors',
  bolts: 'Bolts',
  categories: 'Categories',
  properties: 'Properties',
  local: 'Local',
};

export const createMappingWorkspaceModel = (input: MappingWorkspaceModelInput) => {
  const bindings = buildBindings(input);

  const getRowInsight = (objectTypeName: string, attribute: Attribute): MappingRowInsight => {
    const binding = bindings.get(buildBindingKey(objectTypeName, attribute.pcfAttributeName));
    const currentValue = attribute.revitParam.trim();
    const upstreamValue = binding?.value ?? '';
    const resolved = hasResolvedMapping(attribute);

    if (!binding) {
      if (resolved) {
        return {
          sourceKey: 'local',
          sourceLabel: 'Edited here',
          sourceDetail: 'This mapping currently lives only in the workspace.',
          stateLabel: 'Edited here',
          stateTone: 'info',
          stateDetail: 'No upstream workflow step seeds this row.',
          upstreamValue: '',
          isOverridden: false,
        };
      }

      return {
        sourceKey: 'local',
        sourceLabel: 'Workspace',
        sourceDetail: 'No upstream workflow step seeds this row yet.',
        stateLabel: isRequiredAttribute(attribute) ? 'Missing' : 'Optional',
        stateTone: isRequiredAttribute(attribute) ? 'warning' : 'default',
        stateDetail: isRequiredAttribute(attribute)
          ? 'This required row still needs a mapping before finalizing.'
          : 'This row is available for local mapping if needed.',
        upstreamValue: '',
        isOverridden: false,
      };
    }

    if (resolved && upstreamValue && currentValue === upstreamValue) {
      return {
        sourceKey: binding.sourceKey,
        sourceLabel: binding.sourceLabel,
        sourceDetail: binding.sourceDetail,
        stateLabel: 'Seeded',
        stateTone: 'success',
        stateDetail: 'The workspace still matches the upstream seeded value.',
        upstreamValue,
        isOverridden: false,
        jumpTarget: binding.jumpTarget,
        jumpLabel: binding.jumpLabel,
      };
    }

    if (resolved && upstreamValue && currentValue !== upstreamValue) {
      return {
        sourceKey: binding.sourceKey,
        sourceLabel: binding.sourceLabel,
        sourceDetail: binding.sourceDetail,
        stateLabel: 'Overridden',
        stateTone: 'info',
        stateDetail: 'This row differs from the upstream seeded value and was adjusted locally.',
        upstreamValue,
        isOverridden: true,
        jumpTarget: binding.jumpTarget,
        jumpLabel: binding.jumpLabel,
      };
    }

    if (resolved && !upstreamValue) {
      return {
        sourceKey: binding.sourceKey,
        sourceLabel: binding.sourceLabel,
        sourceDetail: binding.sourceDetail,
        stateLabel: 'Edited here',
        stateTone: 'info',
        stateDetail: 'The source step exists, but this value was completed directly in the workspace.',
        upstreamValue: '',
        isOverridden: false,
        jumpTarget: binding.jumpTarget,
        jumpLabel: binding.jumpLabel,
      };
    }

    if (!resolved && upstreamValue) {
      return {
        sourceKey: binding.sourceKey,
        sourceLabel: binding.sourceLabel,
        sourceDetail: binding.sourceDetail,
        stateLabel: 'Cleared here',
        stateTone: 'warning',
        stateDetail: 'The upstream step supplied a value, but this row is currently cleared in the workspace.',
        upstreamValue,
        isOverridden: true,
        jumpTarget: binding.jumpTarget,
        jumpLabel: binding.jumpLabel,
      };
    }

    return {
      sourceKey: binding.sourceKey,
      sourceLabel: binding.sourceLabel,
      sourceDetail: binding.sourceDetail,
      stateLabel: 'Missing upstream',
      stateTone: isRequiredAttribute(attribute) ? 'warning' : 'default',
      stateDetail: 'The source step exists, but it did not produce a value for this row.',
      upstreamValue: '',
      isOverridden: false,
      jumpTarget: binding.jumpTarget,
      jumpLabel: binding.jumpLabel,
    };
  };

  const getScopeSummary = (objectType: ObjectType): MappingScopeSummary => {
    if (!objectType?.attributes?.length) {
      return DEFAULT_SCOPE_SUMMARY;
    }

    const sourceCounts = new Map<MappingSourceKey, number>();
    const summary = objectType.attributes.reduce<MappingScopeSummary>(
      (accumulator, attribute) => {
        const rowInsight = getRowInsight(objectType.name, attribute);
        sourceCounts.set(rowInsight.sourceKey, (sourceCounts.get(rowInsight.sourceKey) ?? 0) + 1);

        return {
          ...accumulator,
          total: accumulator.total + 1,
          mapped: accumulator.mapped + (hasResolvedMapping(attribute) ? 1 : 0),
          requiredOpen:
            accumulator.requiredOpen +
            (isRequiredAttribute(attribute) && !hasResolvedMapping(attribute) ? 1 : 0),
          overridden: accumulator.overridden + (rowInsight.isOverridden ? 1 : 0),
        };
      },
      { ...DEFAULT_SCOPE_SUMMARY }
    );

    const [dominantSourceKey = 'local'] = [...sourceCounts.entries()].sort(
      (left, right) => right[1] - left[1]
    )[0] ?? [];

    return {
      ...summary,
      dominantSourceKey,
      dominantSourceLabel: SOURCE_LABELS[dominantSourceKey],
    };
  };

  const getSectionSummary = (): MappingWorkspaceSections => ({
    processConditions: {
      label: input.processConfig.processConditionsChecked ? 'On' : 'Off',
      tone: input.processConfig.processConditionsChecked ? 'success' : 'default',
    },
    projectProperties: {
      label: input.processConfig.projectPropertiesChecked ? 'On' : 'Off',
      tone: input.processConfig.projectPropertiesChecked ? 'success' : 'default',
    },
  });

  return {
    getRowInsight,
    getScopeSummary,
    getSectionSummary,
  };
};
